import { ChangeDetectionStrategy, Component, Input, NgZone, OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, UntypedFormArray, UntypedFormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { isEmpty, isNull } from "lodash";
import isEqual from "lodash/isEqual";
import { EMPTY, merge, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, map, switchMap, take } from "rxjs/operators";
import { AOC } from "src/app/models/enum";
import { CandidateSignOffDetails, DialogData, OBComments, SignOffPopup } from "src/app/models/models";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { ProfileService } from "src/app/shared/services/profile.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { ContainerRatingsService } from "../../services/base/container-ratings.services";

// Type the candidateSignature() response so TS knows .data exists
interface CandidateSignatureResponse {
	data?: {
		signature?: {
			data?: number[] | Uint8Array;
			// add other fields if your API returns them (e.g., mimeType)
		};
	};
}

@Component({
	selector: "app-candidate-signoff-no-grading",
	templateUrl: "./candidate-signoff-no-grading.component.html",
	styleUrls: ["./candidate-signoff-no-grading.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CandidateSignoffNoGradingComponent implements OnInit, OnDestroy {
	signoffForm!: UntypedFormGroup;
	parentForm!: UntypedFormArray;
	parentFormArrayIndex = 0;
	panelOpenState = false;
	ratingAVal = 0;
	ratingBVal = 0;
	ratingCVal = 0;
	ratingFinalVal = 0;
	obComments: Array<OBComments> = [];

	@Input() index = 0;
	@Input("signoffForm") set _(value: any) {
		this.signoffForm = value as UntypedFormGroup;
		this.instructorSignaturePreview = "";
		this.traineeSignaturePreview = "";
	}

	instructorSignatureEditMode = true;
	traineeSignatureEditMode = true;
	instructorSignaturePreview?: string = "";
	traineeSignaturePreview?: string = "";
	instructorSignatureFile?: File;

	isOnline = true;

	private subs = new Subscription();

	constructor(
		public sharedService: SharedService,
		private dialog: MatDialog,
		private httpService: HttpService,
		private loadingService: LoaderService,
		private roleService: RoleService,
		private profileService: ProfileService,
		private containerRatingsService: ContainerRatingsService,
		private zone: NgZone,
	) {}

	ngOnInit(): void {
		this.subs.add(this.sharedService.getIsStatusOnline().subscribe((status) => (this.isOnline = status)));

		this.parentForm = this.signoffForm.parent as UntypedFormArray;
		this.parentFormArrayIndex = this.parentForm.controls.findIndex((subform) => subform === this.signoffForm);

		// Programmatic init — silent (no emissions)
		const hasValue = !!this.instructorLicenseNumber?.value;
		const useFlag = this.useInstructorLicense?.value ?? true;
		if (!hasValue) this.toggleLicenseNumber(useFlag, /*silent*/ true);

		// Wait for first stable tick (no timers) then wire reactive bits
		const stableSub = this.zone.onStable.pipe(take(1)).subscribe(() => {
			if (this.category) {
				this.subs.add(
					this.category.valueChanges.subscribe((val: any) => {
						if (val === "Check") {
							// Silent, autosave snapshot will include it
							this.clearanceCheck?.patchValue("N/A", { emitEvent: false });
						}
					}),
				);
			}

			this.attachAutosave();
		});
		this.subs.add(stableSub);
	}

	ngOnDestroy(): void {
		this.subs.unsubscribe();
	}

	get indDraftMode(): boolean {
		return this.containerRatingsService.inDraftMode;
	}

	private attachAutosave() {
		const ctrl = (c: AbstractControl | null | undefined) => (c ? c.valueChanges : EMPTY);

		const changes$ = merge(
			ctrl(this.category),
			ctrl(this.sessionCheck),
			ctrl(this.objectiveCheck),
			ctrl(this.clearanceCheck),
			ctrl(this.useInstructorLicense),
			ctrl(this.instructorLicenseNumber),
		).pipe(
			debounceTime(150),
			map(() => this.snapshot()),
			distinctUntilChanged((a, b) => isEqual(a, b)),
		);

		this.subs.add(changes$.subscribe((patch) => this.updateAssessmentRecommendations(patch)));
	}

	private snapshot(): Partial<CandidateSignOffDetails> {
		return {
			category: this.category?.value ?? "",
			sessionCheck: this.sessionCheck?.value ?? "",
			objectiveCheck: this.objectiveCheck?.value ?? "",
			clearanceCheck: this.clearanceCheck?.value ?? "",
			useInstructorLicense: this.useInstructorLicense?.value,
			instructorLicenseNumber: this.instructorLicenseNumber?.value,
		};
	}

	updateAssessmentRecommendations(data: Partial<CandidateSignOffDetails>) {
		const signOffDetails = this.containerRatingsService.draft?.signOffDetails;

		this.containerRatingsService.updateSelectedDraft({
			signOffDetails: {
				candidateSignOffDetails: {
					...signOffDetails?.candidateSignOffDetails,
					[this.candidateId.value]: {
						...signOffDetails?.candidateSignOffDetails?.[this.candidateId.value],
						...data,
					},
				},
			},
			continuePage: 3,
		});
	}

	// ---- getters ----
	get candidateName() {
		return this.signoffForm.get("candidateName")!;
	}
	get candidateId() {
		return this.signoffForm.get("candidateId")!;
	}
	get traineeAssessmentId() {
		return this.signoffForm.get("traineeAssessmentId")!;
	}
	get ratingA() {
		return this.signoffForm.get("ratingA");
	}
	get ratingB() {
		return this.signoffForm.get("ratingB");
	}
	get ratingC() {
		return this.signoffForm.get("ratingC");
	}
	get finalRating() {
		return this.signoffForm.get("finalRating");
	}
	get category() {
		return this.signoffForm.get("category")!;
	}
	get sessionCheck() {
		return this.signoffForm.get("sessionCheck");
	}
	get objectiveCheck() {
		return this.signoffForm.get("objectiveCheck");
	}
	get clearanceCheck() {
		return this.signoffForm.get("clearanceCheck");
	}
	get trainerName() {
		return this.signoffForm.get("trainerName");
	}
	get trainerSignature() {
		return this.signoffForm.get("trainerSignature");
	}
	get traineeName() {
		return this.signoffForm.get("traineeName");
	}
	get traineeSignature() {
		return this.signoffForm.get("traineeSignature");
	}
	get instructorLicenseNumber() {
		return this.signoffForm.get("instructorLicenseNumber");
	}
	get useInstructorLicense() {
		return this.signoffForm.get("useInstructorLicense");
	}
	get isIAA() {
		return this.containerRatingsService?.draft?.formDetails?.aoc === AOC.iaa;
	}

	// Programmatic patches can be silent during init
	toggleLicenseNumber(isLicenseNumber: boolean, silent = false) {
		this.useInstructorLicense?.patchValue(isLicenseNumber, { emitEvent: !silent });

		const fromProfile = isLicenseNumber
			? this.profileService?.profile?.pilot_license_number
			: this.profileService?.profile?.examiner_number;

		if (fromProfile != null && String(fromProfile).trim().length > 0) {
			this.instructorLicenseNumber?.patchValue(fromProfile, { emitEvent: !silent });
		}
	}

	get instructorHasSignaturePreview(): boolean {
		return !isEmpty(this.instructorSignaturePreview) && !isNull(this.instructorSignaturePreview);
	}

	get traineeHasSignaturePreview(): boolean {
		return !isEmpty(this.traineeSignaturePreview) && !isNull(this.traineeSignaturePreview);
	}

	listallOBComments(val: OBComments) {
		this.obComments.push(val);
	}

	onInstructorSignatureUpdate(e: Event): void {
		const files = (e.target as HTMLInputElement)?.files ?? [];
		if (!isEmpty(files)) {
			this.trainerSignature?.patchValue(files[0]);
		}
	}

	onTraineeSignatureUpdate(e: Event): void {
		const files = (e.target as HTMLInputElement)?.files ?? [];
		if (!isEmpty(files)) {
			this.traineeSignature?.patchValue(files[0]);
		}
	}

	showInstructorSignatureModal(): void {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "signature-pad-modal",
			height: "420px",
			width: "528px",
			hasBackdrop: true,
			data: {
				modalTitle: "Signature",
				modalName: "signaturePadModal",
				modalBtnConfirmText: "Submit",
				modalBtnCancelText: "Cancel",
				signatureData: {
					dataUrl: this.profileService.signaturePreview,
					file: this.profileService.signatureFile,
				},
			},
		});

		const sub = dialogRef.afterClosed().subscribe((result: any) => {
			if (result && result.close) {
				const dataUrl = result?.signature?.dataUrl;
				const file = result?.signature?.file;

				this.trainerSignature?.patchValue(file);
				this.containerRatingsService.syncSelectedDraft();

				if (dataUrl) {
					this.instructorSignaturePreview = result.signature.dataUrl;
				}
			}
			sub.unsubscribe();
		});
	}

	showTraineeSignatureModal(signatureBytes?: number[] | Uint8Array): void {
		const baseDialogData: DialogData = {
			modalTitle: "Signature",
			modalName: "signaturePadModal",
			modalBtnConfirmText: "Submit",
			modalBtnCancelText: "Cancel",
		};

		const openDialog = (dialogData: DialogData) => {
			const dialogRef = this.dialog.open(CbtaModalComponent, {
				panelClass: "signature-pad-modal",
				height: "420px",
				width: "528px",
				hasBackdrop: true,
				data: dialogData,
			});

			dialogRef.afterClosed().subscribe((result: any) => {
				if (result?.close) {
					const file = result?.signature?.file;
					const dataUrl = result?.signature?.dataUrl;

					this.traineeSignature?.patchValue(file);
					this.containerRatingsService.syncSelectedDraft();

					if (dataUrl) {
						this.traineeSignaturePreview = dataUrl;
					}
				}
			});
		};

		if (!signatureBytes) {
			openDialog(baseDialogData);
			return;
		}

		const uint8Array = new Uint8Array(signatureBytes);
		const blob = new Blob([uint8Array], { type: "image/png" });
		const file = new File([blob], "signature.png", { type: "image/png" });

		const reader = new FileReader();
		reader.onload = () => {
			const dataUrl = reader.result as string;

			openDialog({
				...baseDialogData,
				signatureData: { dataUrl, file },
			});
		};

		reader.readAsDataURL(blob);
	}

	get signingIsValid(): boolean {
		return (
			(this.category.valid && this.sessionCheck?.valid && this.objectiveCheck?.valid && this.clearanceCheck?.valid) ??
			false
		);
	}

	async signOffModel(user: string) {
		if (!navigator.onLine) {
			this.containerRatingsService.blockSubmitIfOffline(true);
			return;
		}

		this.containerRatingsService.syncSelectedDraft();

		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "signoff-modal",
			height: "400px",
			width: "460px",
			hasBackdrop: true,
			data: {
				modalName: "signOffModal",
				modalTitle: "Sign Off",
				modalContent: "",
				modalBtnConfirmText: "Sign",
				modalBtnCancelText: "Cancel",
				disableClose: true,
			},
		});

		const sub = dialogRef.afterClosed().subscribe((result: any) => {
			if (result) {
				const draftId = this.containerRatingsService.draft?.id;
				const signOffData: SignOffPopup = {
					instructorId: this.roleService.userDetails.id,
					secretKey: result.signOffData.secretKey,
					staffNumber: result.signOffData.staffNumber,
				};

				this.loadingService.setLoading(true);

				this.containerRatingsService.syncSelectedDraft();

				if (user === "trainer") {
					console.log("trainer sign off");
					this.httpService.instructorSignoffValidation(signOffData).subscribe(() => {
						this.loadingService.setLoading(false);
						this.showInstructorSignatureModal();
					});
				} else if (user === "trainee") {
					this.httpService
						.candidateSignoff(this.traineeAssessmentId.value, signOffData)
						.pipe(
							switchMap(
								() =>
									// Type the inner call so TS knows the shape
									this.httpService.candidateSignature(
										result.signOffData.staffNumber,
									) as unknown as import("rxjs").Observable<CandidateSignatureResponse>,
							),
						)
						.subscribe({
							next: (resp: CandidateSignatureResponse) => {
								this.loadingService.setLoading(false);
								const signatureBytes = resp.data?.signature?.data;
								this.showTraineeSignatureModal(signatureBytes);
							},
							error: () => {
								this.loadingService.setLoading(false);
							},
						});
				}
				this.loadingService.setLoading(false);
			}
			sub.unsubscribe();
		});
	}
}
